/**
 * This provider encapsulates layout-specific logic internally, providing a
 * single interface regardless of which layout mode is active.
 *
 * For compare mode, uses compareConfigUtils for column-specific updates.
 * For other modes, uses simple shared updates.
 */

import {clamp, isNil} from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {type MediaBrowserPanelConfig} from '../../common';
import {useMediaKeys} from '../../hooks/useMediaKeys';
import {useUpdateConfigWithMediaKeys} from '../../hooks/useUpdateConfigWithMediaKeys';
import {
  type MediaCount,
  type MediaKey,
  type RunName,
  type SliderStep,
} from '../../types';
import {isValidMediaKeyOption} from '../../utils/isValidMediaKeyOption';
import {runHasMediaKey} from '../../utils/runUtils';
import {
  pickNonEmptyIndices,
  pickSliderStepsOrSteps,
  resolveCurrentStep,
} from '../../utils/stepUtils';
import {type GridCell} from '../types';
import {
  updateColumnIndex,
  updateColumnMediaKey,
  updateColumnRun,
  updateColumnStep,
  updateSharedRun,
} from './compareConfigUtils';
import {
  LayoutConfigContext,
  type LayoutConfigContextValue,
  type LayoutConfigProviderProps,
} from './LayoutConfigContext';

export const LayoutConfigProvider: React.FC<LayoutConfigProviderProps> = ({
  mediaType,
  config,
  availableRuns,
  availableMediaKeys,
  stepMeta,
  gridData,
  controlData,
  rowData,
  updateConfig,
  children,
  columnWidth,
  configMode = false,
}) => {
  const updateConfigWithMediaKeys = useUpdateConfigWithMediaKeys();
  const {keyTypes} = useMediaKeys();

  // ============================================================================
  // Getters
  // ============================================================================
  const getCell = useCallback(
    (columnIndex?: number, rowIndex?: number): GridCell | undefined => {
      if (columnIndex != null && rowIndex != null) {
        const cellStartIndex = gridData?.metadata?.cellStartIndex ?? 0;
        const rowOffset = Math.floor(cellStartIndex / config.columnCount);
        return gridData?.rows?.[rowIndex + rowOffset]?.cells?.[columnIndex];
      }
      return undefined;
    },
    [gridData, config.columnCount]
  );

  // ============================================================================
  // Getters
  // ============================================================================

  const getRun = useCallback(
    (columnIndex?: number, rowIndex?: number) => {
      let runName = undefined;
      if (columnIndex != null && rowIndex != null) {
        const cell = gridData?.rows?.[rowIndex]?.cells?.[columnIndex];
        if (cell && !cell.isEmpty) {
          runName = cell.tileSpec.run.name;
        }
      } else if (columnIndex != null) {
        runName = controlData?.[columnIndex]?.run;
      } else if (rowIndex != null) {
        runName = rowData?.[rowIndex]?.run;
      } else if (config.mode === 'compare') {
        runName = config.compareSettings?.runName;
      }
      return runName ?? availableRuns[0]?.name;
    },
    [controlData, config, availableRuns, gridData, rowData]
  );

  // Column-specific: single media key (requires columnIndex)
  const getMediaKey = useCallback(
    (columnIndex: number) => {
      if (controlData?.[columnIndex] !== undefined) {
        return controlData[columnIndex].mediaKey;
      }
      // Fallback for modes that don't yet populate controlData
      const numSharedMediaKeys = config.mediaKeys?.length ?? 0;
      const keyIndex =
        numSharedMediaKeys > 1
          ? clamp(columnIndex, 0, numSharedMediaKeys - 1)
          : 0;
      return config.mediaKeys?.[keyIndex];
    },
    [controlData, config]
  );

  // Top-level: multiple media keys (no columnIndex)
  const getMediaKeys = useCallback(() => {
    return config.mediaKeys ?? [];
  }, [config.mediaKeys]);

  const getStep = useCallback(
    (columnIndex?: number, rowIndex?: number): number => {
      const sliderKey = config.mediaSliderKey ?? '_step';
      const isDefaultSlider = sliderKey === '_step';
      let step: number | undefined = undefined;

      const cell = getCell(columnIndex, rowIndex);
      if (cell && !cell.isEmpty) {
        step = isDefaultSlider ? cell.step : cell.tileSpec?.mediaSliderKeyValue;
      }
      if (isNil(step) && !isNil(columnIndex)) {
        step = controlData?.[columnIndex]?.step;
      } else if (isNil(step) && !isNil(rowIndex)) {
        step = rowData?.[rowIndex]?.step;
      }
      const maxStep = isDefaultSlider ? stepMeta?.max : stepMeta?.maxSliderStep;
      return step ?? config.stepIndex ?? maxStep ?? 0;
    },
    [controlData, getCell, config, stepMeta, rowData]
  );

  const getIndex = useCallback(
    (columnIndex?: number, rowIndex?: number) => {
      let index = undefined;
      const cell = getCell(columnIndex, rowIndex);
      if (cell && !cell.isEmpty) {
        index = cell.mediaIndex;
      }
      if (isNil(index) && columnIndex != null) {
        index = controlData?.[columnIndex]?.index;
      }
      if (isNil(index) && rowIndex != null) {
        index = rowData?.[rowIndex]?.index;
      }
      return index ?? config.mediaIndex ?? 0;
    },
    [controlData, config, rowData, getCell]
  );

  // ============================================================================
  // Collections
  // ============================================================================

  // Maps are pre-built in useStepMeta — no secondary processing needed here
  const mediaCountByRunKeyStep = useMemo(() => {
    return (
      stepMeta?.mediaCountByRunKeyStep ??
      new Map<RunName, Map<MediaKey, Map<SliderStep, MediaCount>>>()
    );
  }, [stepMeta]);

  const getAvailableRuns = useCallback(
    (columnIndex?: number) => {
      if (columnIndex != null) {
        const mediaKey = getMediaKey(columnIndex);
        if (mediaKey != null) {
          return availableRuns.filter(run => runHasMediaKey(run, mediaKey));
        }
      }

      return availableRuns;
    },
    [availableRuns, getMediaKey]
  );

  const getAvailableSteps = useCallback(
    (columnIndex?: number) => {
      if (!stepMeta) {
        return undefined;
      }

      // Use precomputed availableSteps when this column has data
      if (columnIndex != null && controlData?.[columnIndex] !== undefined) {
        return controlData[columnIndex].availableSteps;
      }

      // For gallery/grid modes or when no columnIndex, return global steps
      return pickSliderStepsOrSteps(stepMeta.sliderSteps, stepMeta.steps);
    },
    [stepMeta, controlData]
  );

  const getAvailableIndices = useCallback(
    (columnIndex?: number) => {
      if (!stepMeta) {
        return undefined;
      }

      // Use precomputed availableIndices when this column has data
      if (columnIndex != null && controlData?.[columnIndex] !== undefined) {
        return controlData[columnIndex].availableIndices;
      }

      // For gallery/grid modes or when no columnIndex, return global indices
      return pickNonEmptyIndices(stepMeta.indices);
    },
    [stepMeta, controlData]
  );

  // Available options
  const getAvailableMediaKeys = useCallback(
    (columnIndex?: number) => {
      // If no columnIndex provided, return all available keys
      if (columnIndex == null) {
        return availableMediaKeys;
      }
      // Filter by panel's media type
      return availableMediaKeys.filter(key =>
        isValidMediaKeyOption(key, keyTypes, [], [mediaType])
      );
    },
    [availableMediaKeys, mediaType, keyTypes]
  );

  // ============================================================================
  // Setters
  // ============================================================================

  const setRun = useCallback(
    (runName: string, columnIndex?: number) => {
      const runControlMode = config.compareSettings?.tileControls?.run;

      if (runControlMode === 'linked') {
        const updates = updateSharedRun(config, runName);
        updateConfig(updates);
      } else if (columnIndex != null) {
        const updates = updateColumnRun(config, columnIndex, runName);
        updateConfig(updates);
      }
    },
    [config, updateConfig]
  );

  const setMediaKey = useCallback(
    (mediaKey: string, columnIndex: number) => {
      const tileControlMode = config.compareSettings?.tileControls?.mediaKey;

      let updates: Partial<MediaBrowserPanelConfig> = {mediaKeys: [mediaKey]};
      if (tileControlMode === 'individual') {
        // In individual mode, update the column's mediaKey
        // updateColumnMediaKey handles updating config.mediaKeys to union of all column keys
        const columnUpdates = updateColumnMediaKey(
          config,
          columnIndex,
          mediaKey
        );
        updates = {...updates, ...columnUpdates};
      }
      updateConfigWithMediaKeys(updates, config, updateConfig);
    },
    [config, updateConfig, updateConfigWithMediaKeys]
  );

  const setMediaKeys = useCallback(
    (mediaKeys: string[]) => {
      updateConfigWithMediaKeys({mediaKeys}, config, updateConfig);
    },
    [config, updateConfig, updateConfigWithMediaKeys]
  );

  const setStep = useCallback(
    (value: number, columnIndex?: number) => {
      if (columnIndex != null) {
        const sharedStep = resolveCurrentStep(
          config.stepIndex,
          stepMeta,
          config.mediaSliderKey ?? '_step'
        );
        let newConfig = updateColumnStep(
          config,
          columnIndex,
          value,
          sharedStep
        );

        // If the column's current index is no longer valid at the new step, reset it.
        // column.mediaKey is only set in individual key mode.
        const column = config.compareSettings?.columns?.[columnIndex];
        // we do want the fallbacks for the run/media key
        const runName = getRun(columnIndex);
        const mediaKey = getMediaKey(columnIndex);
        if (runName && mediaKey) {
          const countAtNewStep =
            mediaCountByRunKeyStep.get(runName)?.get(mediaKey)?.get(value) ?? 0;
          // we do NOT want the fallback for the index
          const currentIndex = column?.index ?? 0;
          // Only reset the column index if we have data at this run + key + step,
          // the column has index data, and the index exceeds it.
          if (countAtNewStep > 0 && currentIndex >= countAtNewStep) {
            newConfig = updateColumnIndex(
              newConfig,
              columnIndex,
              countAtNewStep - 1
            );
          }
        }

        updateConfig(newConfig);
      } else {
        updateConfig({stepIndex: value});
      }
    },
    [
      config,
      stepMeta,
      updateConfig,
      mediaCountByRunKeyStep,
      getRun,
      getMediaKey,
    ]
  );

  const setIndex = useCallback(
    (value: number, columnIndex?: number) => {
      if (columnIndex != null) {
        const updates = updateColumnIndex(config, columnIndex, value);
        updateConfig(updates);
      } else {
        updateConfig({mediaIndex: value});
      }
    },
    [config, updateConfig]
  );

  const contextValue: LayoutConfigContextValue = useMemo(
    () => ({
      getStep,
      setStep,
      getAvailableSteps,
      getIndex,
      setIndex,
      getAvailableIndices,
      getRun,
      setRun,
      getAvailableRuns,
      getMediaKey,
      setMediaKey,
      getMediaKeys,
      setMediaKeys,
      getAvailableMediaKeys,
      columnWidth,
      mediaType,
      getCell,
      isPreview: configMode,
    }),
    [
      getStep,
      setStep,
      getAvailableSteps,
      getIndex,
      setIndex,
      getAvailableIndices,
      getRun,
      setRun,
      getAvailableRuns,
      getMediaKey,
      setMediaKey,
      getMediaKeys,
      setMediaKeys,
      getAvailableMediaKeys,
      mediaType,
      columnWidth,
      getCell,
      configMode,
    ]
  );

  return (
    <LayoutConfigContext.Provider value={contextValue}>
      {children}
    </LayoutConfigContext.Provider>
  );
};
